import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { EASE } from "./Reveal";
import { EMAIL } from "../../data/content";

const LINES = ["Personalised", "K–12 tutoring,", "by recent HSC", "high-achievers."];
const HERO_IMG =
    "https://images.unsplash.com/photo-1572979733972-a45da9dd7918?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxzdHVkeWluZyUyMGxpYnJhcnklMjB1bml2ZXJzaXR5JTIwYm9va3MlMjBzeWRuZXklMjBhcmNoaXRlY3R1cmUlMjBhY2FkZW1pYyUyMGVkaXRvcmlhbHxlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3ODgyMjk5MTV8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ text, i }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.35 + i * 0.12, ease: EASE }}
        >
            {text}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

    return (
        <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-16">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-6 lg:left-10 top-0 bottom-0 w-px bg-ink/10" />
                <div className="absolute right-6 lg:right-10 top-0 bottom-0 w-px bg-ink/10" />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center py-24">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="flex items-center gap-3 mb-8"
                    >
                        <span className="w-10 h-px bg-oxblood" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate2">
                            North Shore Scholars · Est. 2026
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="font-serif text-5xl sm:text-6xl lg:text-[5.25rem] leading-[1.02] tracking-tight text-ink"
                    >
                        {LINES.map((l, i) => (
                            <MaskedLine key={l} text={l} i={i} />
                        ))}
                    </h1>

                    <motion.p
                        data-testid="hero-subheadline"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                        className="mt-8 max-w-xl text-base md:text-lg text-slate2 leading-relaxed"
                    >
                        Personalised one-on-one tutoring from Oliver &amp; Ashton — UNSW
                        Engineering students and Knox Grammar graduates with ATARs of{" "}
                        <em className="font-serif text-ink">97 and 95</em>. Online and
                        in-person across Sydney's North Shore.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contact"
                            onClick={(e) => {
                                e.preventDefault();
                                window.__lenis?.scrollTo("#contact", { offset: -70 });
                            }}
                            data-testid="hero-enquire-button"
                            className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-navy transition-colors duration-300"
                        >
                            Enquire today
                            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={1.75} />
                        </a>
                        <a
                            href="#pricing"
                            onClick={(e) => {
                                e.preventDefault();
                                window.__lenis?.scrollTo("#pricing", { offset: -70 });
                            }}
                            data-testid="hero-pricing-link"
                            className="inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink hover:border-oxblood hover:text-oxblood transition-colors duration-300"
                        >
                            From $55/hr
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.4 }}
                        className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
                    >
                        {["Knox Grammar 2025", "UNSW Engineering", "ATAR 97 & 95"].map((b) => (
                            <span
                                key={b}
                                data-testid={`hero-badge-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate2 border-b border-ink/15 pb-1"
                            >
                                {b}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative hidden lg:block">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
                        className="relative"
                    >
                        <div className="overflow-hidden">
                            <motion.img
                                src={HERO_IMG}
                                alt="Students studying in a university library"
                                data-testid="hero-image"
                                style={{ y: imgY }}
                                className="w-full h-[540px] object-cover scale-110 grayscale contrast-110"
                            />
                        </div>
                        <motion.div
                            style={{ y: cardY }}
                            data-testid="hero-atar-card"
                            className="absolute -bottom-8 -left-10 bg-ink text-bone p-7 shadow-2xl"
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin className="w-3.5 h-3.5 text-bone/60" strokeWidth={1.75} />
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/60">
                                    North Shore, Sydney
                                </span>
                            </div>
                            <div className="font-serif text-5xl leading-none">
                                97 <span className="text-bone/40 text-3xl">&amp;</span> 95
                            </div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
                                HSC ATARs — Class of 2025
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
